import { useCallback, useEffect, useState } from "react";

export type ThemePreference = "light" | "dark" | "system";
const KEY = "ui-theme";

const read = (): ThemePreference => {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "system";
  } catch {
    return "system";
  }
};

const apply = (pref: ThemePreference) => {
  const dark =
    pref === "dark" || (pref === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", dark);
};

/** Light / dark / system, stored per browser (same key the old sidebar used). */
export const useThemePreference = () => {
  const [pref, setPref] = useState<ThemePreference>(read);

  useEffect(() => {
    apply(pref);
    if (pref !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => apply("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [pref]);

  const set = useCallback((next: ThemePreference) => {
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* private mode: keep it for this tab only */
    }
    setPref(next);
  }, []);

  const cycle = useCallback(() => {
    set(pref === "light" ? "dark" : pref === "dark" ? "system" : "light");
  }, [pref, set]);

  return { pref, set, cycle };
};
